'use client';

import React from 'react';
import { AnimatedTitle } from '../../animated-title';

import type { LandscapeChapterHero } from '@/types';
import Link from 'next/link';
import { getMediaData } from '@/lib/utils/media.utils';

interface LandscapeChapterHeroProps {
  layout: LandscapeChapterHero;
}

export const Landscape_ChapterHero: React.FC<LandscapeChapterHeroProps> = ({ layout }) => {
  const bgImage = getMediaData(layout.image);

  return (
    <section className="w-full">
      <div
        className={`p-[15px] rounded-md bg-white mb-[12px] flex flex-col justify-between relative `}
        style={{
          background: (bgImage.url ? `url('${bgImage.url}')` : ' ') + (layout.bgColor ? layout.bgColor : 'white'),
          color: layout.textColor ? layout.textColor : 'black',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          minHeight: `${layout.minHeight}px`,
        }}
      >
        {layout.titleLeftTop ? (
          <div className="static lg:absolute top-2 left-2">
            <AnimatedTitle>{layout.titleLeftTop}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}
        {layout.titleCenterTop ? (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
            <AnimatedTitle>{layout.titleCenterTop}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}

        {/* Title positioned in the center of the left side */}
        <div className="static lg:absolute left-3 lg:top-1/2 lg:transform lg:-translate-y-1/2">
          <h2 className="text-3xl md:text-4xl font-superior uppercase max-w-lg">{layout.mainTitle}</h2>
        </div>

        {layout.titleCenterBottom ? (
          <div className="static lg:absolute bottom-2 left-1/2 transform -translate-x-1/2">
            <AnimatedTitle marginClass="mb-0">{layout.titleCenterBottom}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}

        {layout.titleLeftBottom ? (
          <div className="static lg:absolute bottom-2 left-2">
            <AnimatedTitle marginClass="mb-0">{layout.titleLeftBottom}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}
        {layout.titleRightTop ? (
          <div className="absolute top-2 right-2">
            <AnimatedTitle marginClass="mb-0">{layout.titleRightTop}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}

        {layout.titleRightBottom && !layout.urlRightBottom ? (
          <div className="absolute bottom-2 right-2">
            <AnimatedTitle marginClass="mb-0">{layout.titleRightBottom}</AnimatedTitle>
          </div>
        ) : (
          ''
        )}

        {/* Description section - absolutely positioned */}
        {layout.leftBottomDescription ? (
          <div className="static lg:absolute bottom-3 left-3 max-w-lg mt-4">
            <p className="text-[10px] font-mono mb-2">[ DESCRIPTION ]</p>
            <p className="text-[10px] font-mono leading-relaxed">{layout.leftBottomDescription}</p>
          </div>
        ) : (
          ''
        )}

        {/* Countdown timer positioned aligned with tags */}
        {layout.centerType === 'text' ? (
          <div
            className="static lg:absolute lg:top-1/2 lg:transform lg:-translate-y-1/2 left-1/2"
            
          >
            <div className="max-w-lg">
              {layout.centerTitle && (
                <p className="text-[10px] font-mono mb-2">{layout.centerTitle}</p>
              )}
              {layout.centerDescription && (
                <p className="text-[10px] font-mono leading-relaxed">{layout.centerDescription}</p>
              )}
            </div>
          </div>
        ) : (
          ''
        )}

        {/* Tags section - absolutely positioned */}
        {layout.centerType === 'labels' && layout.labels.length > 0 ? (
          <div
            className="static lg:absolute bottom-4 mt-4 lg:bottom-1/2 lg:transform lg:-translate-y-1/2"
            style={{ left: 'calc(50% - 100px)' }}
          >
            <div className="flex flex-wrap flex-col gap-1 gap-y-3 max-w-lg items-start">
              {layout.labels?.map((tag, index) => {
                const TagElement = tag.url ? 'a' : 'span';
                return (
                  <div
                    key={index}
                    className="flex text-[10px] grid-cols-2 flex-col lg:flex-row items-start lg:items-center gap-4 gap-y-1 lg:gap-y-20 mb-2 last:mb-0 lg:mb-0"
                  >
                    {tag.title && <span className="inline-flex min-w-[100px] uppercase">{tag.title}</span>}
                    <div>
                      <TagElement
                        href={tag.url || undefined}
                        className={`px-1 py-[2px] rounded-sm ${tag.url ? 'cursor-pointer' : ''} transition-colors`}
                        style={{
                          backgroundColor: tag.bgColor || 'transparent',
                          color: tag.textColor || '#E5E5E5',
                          border: '1px solid ' + (tag.bgColor || '#434343'),
                        }}
                      >
                        {tag.value && <span className="font-bold">{tag.value}</span>}
                      </TagElement>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          ''
        )}

        {layout.titleRightBottom && layout.urlRightBottom ? (
          <div className="static lg:absolute bottom-2 right-2 mt-4">
            <AnimatedTitle marginClass="mb-0">
              <Link
                href={layout.urlRightBottom}
                className="text-[11px] bg-white hover:bg-gray-200 
            rounded-md cursor-pointer transition-color text-black px-3 py-1 inline-flex
            "
              >
                {layout.titleRightBottom}
              </Link>
            </AnimatedTitle>
          </div>
        ) : (
          ''
        )}
      </div>
    </section>
  );
};
